import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddPayoutAccountIdentifierFormProps,
  IdentifierForm,
} from './AddPayoutAccountIdentifierForm.types';
import { Select } from '../../sharedComponents/Select/Select';
import {
  CurrencyUnit,
  currencyUnitToSymbol,
} from '../../utils/types/Currency/Currency.types';
import { CLEARED_COUNTRY_CODES as acceptedCountries } from '../../utils/types/CountryCode/CountryCode.types';
import { useStore } from '../../store/hooks';
import { FORM_ID } from '../../utils/validate';

const AddPayoutAccountIdentifierForm: React.FC<
  AddPayoutAccountIdentifierFormProps
> = ({ handleFormChange, handleFormComplete }) => {
  const [identifierForm, setIdentifierForm] = useState<IdentifierForm>({
    countryCode: '',
    bankCurrency: undefined,
  });
  const { t } = useTranslation();
  const {
    user: { user },
  } = useStore();
  const formID = FORM_ID.IDENTIFIER;

  const locale = useMemo(() => {
    const candidate = user.langLocale || 'en';
    const [supported] = Intl.DisplayNames.supportedLocalesOf([candidate]);
    return supported ?? 'en';
  }, [user.langLocale]);

  const dn = useMemo(
    () => new Intl.DisplayNames([locale], { type: 'region' }),
    [locale],
  );

  const countryItems = useMemo(
    () =>
      [...acceptedCountries].sort((a, b) =>
        (dn.of(a) ?? a).localeCompare(dn.of(b) ?? b, locale),
      ),
    [dn, locale],
  );

  const currencyArray = useMemo(
    () => Object.values(CurrencyUnit) as CurrencyUnit[],
    [],
  );

  const updateForm = (nextForm: IdentifierForm) => {
    if (nextForm.countryCode && nextForm.bankCurrency) {
      handleFormComplete(true);
      handleFormChange({ id: formID, form: nextForm });
    }
  };

  const handleCountryChange = (code: string | null) => {
    setIdentifierForm((prev): IdentifierForm => {
      const nextForm: IdentifierForm = {
        ...prev,
        countryCode: code ?? '',
      };
      updateForm(nextForm);
      return nextForm;
    });
  };

  const handleCurrencyChange = (unit: CurrencyUnit | null) => {
    setIdentifierForm((prev): IdentifierForm => {
      const nextForm: IdentifierForm = {
        ...prev,
        bankCurrency: unit ?? undefined,
      };
      updateForm(nextForm);
      return nextForm;
    });
  };

  return (
    <>
      <Select
        items={countryItems}
        value={identifierForm?.countryCode || undefined}
        onChange={handleCountryChange}
        showLabel={true}
        label={t('sunshine.label.country_of_bank')}
        itemToString={(code) =>
          code ? `${dn.of(code) ?? code} (${code})` : ''
        }
      />

      <Select
        items={currencyArray}
        showLabel={true}
        value={identifierForm.bankCurrency || undefined}
        onChange={handleCurrencyChange}
        label={t('sunshine.label.transfer_currency')}
        itemToString={(unit) =>
          unit ? `${unit.toUpperCase()} (${currencyUnitToSymbol(unit)})` : ''
        }
      />
    </>
  );
};

export default AddPayoutAccountIdentifierForm;
